// frontend/src/components/Samples/MyTasksList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { lineOperationsAPI, customersAPI, usersAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import SearchableSelect from '../Common/SearchableSelect';
import {
  FiCheckCircle, FiClock, FiAlertCircle, FiEdit2, FiList, FiSearch, FiX,
} from 'react-icons/fi';
import Toast from '../Common/Toast';
import { useToast } from '../../hooks/useToast';
import MyTasksEditModal from './MyTasksEditModal';
import LineOperationsModal from './LineOperationsModal';
import '../Settings/Modal.css';
import './MyTasksList.css';
import { TASK_STATUS_META, getTaskStatusClass } from '../../constants/statusMeta';

const STATUS_OPTIONS = [
  { value: '', label: 'Pendentes & Em Curso' },
  ...Object.entries(TASK_STATUS_META).map(([k, v]) => ({ value: k, label: v.label })),
  { value: 'ALL', label: 'Todos os estados' },
];
const MyTasksList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser } = useAuth();

  // Default filters: pre-fill with the logged-in user so the page opens
  // showing only their tasks. clearFilters() resets back to this baseline,
  // NOT to fully empty — clearing the user filter shows all users' tasks.
  const getDefaultFilters = useCallback(() => ({
    status:          '',
    user_id:         authUser?.user_id ? String(authUser.user_id) : '',
    area_id:         '',
    operation_id:    '',
    sample_order_no: '',
    customer_id:     '',
    erp_product_code:     '',
  }), [authUser?.user_id]);

  // ── Table state ──
  const [tasks,        setTasks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalItems,   setTotalItems]   = useState(0);

  // ── Filter state ──
  const [filters,     setFilters]    = useState(() => ({
    status:          '',
    user_id:         authUser?.user_id ? String(authUser.user_id) : '',
    area_id:         '',
    operation_id:    '',
    sample_order_no: '',
    customer_id:     '',
    erp_product_code:     '',
  }));
  // Debounced text fields (order no / erp product) are applied on Enter or blur
  const [textDraft,   setTextDraft]  = useState({ sample_order_no: '', erp_product_code: '' });

  // ── Lookup state for filter dropdowns ──
  const [users,       setUsers]      = useState([]);
  const [areas,       setAreas]      = useState([]);
  const [operations,  setOperations] = useState([]);
  const [customers,   setCustomers]  = useState([]);

  // ── Modal state ──
  const [editTask,       setEditTask]       = useState(null);   // MyTasksEditModal
  const [lineModal,      setLineModal]      = useState(null);   // LineOperationsModal

  const { toasts, removeToast, toast_success, toast_error } = useToast();

  // ── Load filter lookup data once ──────────────────────────────────────────
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [areasRes, opsRes, customersRes, usersRes] = await Promise.all([
          lineOperationsAPI.getAreas(),
          lineOperationsAPI.getOperationTypes(),
          customersAPI.getAll(),
          usersAPI.getAll(),
        ]);

        // Each API may return data under .data, .users, .items, etc.
        // Always fall back to [] so .map() never receives undefined.
        if (areasRes?.data?.success)
          setAreas(areasRes.data.data || []);

        if (opsRes?.data?.success)
          setOperations(opsRes.data.data || []);

        if (customersRes?.data?.success)
          setCustomers(customersRes.data.data || []);

        // usersAPI.getAll() returns { success, users: [...] } in this codebase
        if (usersRes?.data?.success)
          setUsers(usersRes.data.users || usersRes.data.data || []);

      } catch {
        // Non-critical — filters still work with empty dropdowns
      }
    };
    loadLookups();
  }, []);

  // ── Load tasks ────────────────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      const q = new URLSearchParams({ page: currentPage, limit: itemsPerPage });
      if (filters.status)          q.set('status',          filters.status);
      if (filters.user_id)         q.set('user_id',         filters.user_id);
      if (filters.area_id)         q.set('area_id',         filters.area_id);
      if (filters.operation_id)    q.set('operation_id',    filters.operation_id);
      if (filters.sample_order_no) q.set('sample_order_no', filters.sample_order_no);
      if (filters.customer_id)     q.set('customer_id',     filters.customer_id);
      if (filters.erp_product_code)     q.set('erp_product_code',     filters.erp_product_code);

      const res = await lineOperationsAPI.getMyTasks(q.toString());

      if (res.data.success) {
        setTasks(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
        setTotalItems(res.data.pagination.totalItems);
      }
      setError(null);
    } catch {
      setError(t('messages.error_load', 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filters, t]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Auto-open a specific task when URL has ?task=<public_id>
  // e.g. clicked from a notification email link
  useEffect(() => {
    const taskPublicId = searchParams.get('task');
    if (!taskPublicId || loading || !tasks.length) return;
    const found = tasks.find(t => t.public_id === taskPublicId);
    if (found) {
      setEditTask(found);
      // Clean the param from URL so reloads don't re-open the modal
      navigate(window.location.pathname, { replace: true });
    }
  }, [searchParams, tasks, loading]);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleTextCommit = (field) => {
    // Only trigger a reload if the value actually changed
    if (textDraft[field] !== filters[field]) {
      handleFilterChange(field, textDraft[field]);
    }
  };

  const hasActiveFilters = (() => {
    const def = getDefaultFilters();
    return Object.keys(def).some(k => filters[k] !== def[k]);
  })();

  const clearFilters = () => {
    setFilters(getDefaultFilters());
    setTextDraft({ sample_order_no: '', erp_product_code: '' });
    setCurrentPage(1);
  };

  // ── Task save ─────────────────────────────────────────────────────────────
  const handleSaveTask = async (publicId, form) => {
    // apply_to is handled here, not sent to the single-task update
    const { apply_to: applyTo, ...taskForm } = form;
    await lineOperationsAPI.update(publicId, taskForm);

    if (applyTo?.public_ids?.length) {
      try {
        await lineOperationsAPI.applyToOthers(publicId, {
          target_public_ids: applyTo.public_ids,
          ...applyTo.fields,
        });
        const n = applyTo.public_ids.length;
        toast_success(`Tarefa guardada e aplicada a mais ${n} linha${n > 1 ? 's' : ''}`);
      } catch (err) {
        toast_error(err.response?.data?.error
          ? `Tarefa guardada, mas não foi possível aplicar às outras linhas: ${err.response.data.error}`
          : 'Tarefa guardada, mas não foi possível aplicar às outras linhas');
      }
    } else {
      toast_success('Tarefa guardada');
    }
    setEditTask(null);
    await loadTasks();
  };

  // ── Mark done shortcut ────────────────────────────────────────────────────
  const markDone = async (e, task) => {
    e.stopPropagation();
    try {
      const today = new Date().toISOString().slice(0, 10);
      await lineOperationsAPI.update(task.public_id, {
        status: 'COMPLETED',
        real_end_date: today,
      });
      toast_success('Tarefa concluída');
      await loadTasks();
    } catch {
      toast_error('Erro ao concluir tarefa');
    }
  };

  const isOverdue = (task) =>
    task.planned_end_date
    && task.status !== 'COMPLETED'
    && task.status !== 'CANCELLED'
    && new Date(task.planned_end_date) < new Date();

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      header: t('areas.area', 'Area'),
      field: 'area_code',
      className: 'code-column',
      render: (row) => <span className="task-area-badge">{row.area_code}</span>,
    },
    {
      header: t('areas.operation', 'Tarefa'),
      field: 'operation_label',
      render: (row) => (
        <div className="task-op-cell">
          <span className="task-op-label">{row.operation_label}</span>
          {row.operation_instructions && <span className="task-obs-hint" title={row.operation_instructions}>📝</span>}
        </div>
      ),
    },
    {
      header: t('sample_orders.order_no', 'Pedido'),
      field: 'sample_order_no',
      className: 'code-column',
      render: (row) => (
        <span
          className="task-order-link"
          onClick={e => { e.stopPropagation(); navigate(`/samples/orders/${row.order_public_id}`); }}
        >
          {row.sample_order_no}
        </span>
      ),
    },
    {
      header: t('sample_orders.matrix_line_no', 'Linha'),
      field: 'matrix_line_no',
      render: (row) => row.matrix_line_no || '—',
    },    
    {
      header: t('sample_orders.customer', 'Cliente'),
      field: 'customer_name',
      render: (row) => row.customer_name || '—',
    },
    {
      header: t('areas.product', 'Produto ERP'),
      field: 'product_info',
      render: (row) => row.erp_product_code || '—',
    },
    {
      header: t('areas.planned_end', 'Fim Planeado'),
      field: 'planned_end_date',
      render: (row) => {
        if (!row.planned_end_date) return '—';
        const overdue = isOverdue(row);
        return (
          <span className={`task-date${overdue ? ' overdue' : ''}`}>
            {overdue && <FiAlertCircle size={11} />}
            {new Date(row.planned_end_date).toLocaleDateString('pt-PT')}
          </span>
        );
      },
    },
    {
      header: 'Responsável',
      field: 'responsible_name',
      render: (row) => row.responsible_name || '—',
    },
    {
      header: t('status', 'Estado'),
      field: 'status',
      render: (row) => {
        const m = TASK_STATUS_META[row.status] || TASK_STATUS_META.NEW;
        const icon = row.status === 'COMPLETED' ? <FiCheckCircle size={12}/>
                   : row.status === 'ON_HOLD'   ? <FiAlertCircle size={12}/>
                   : row.status === 'CANCELLED' ? null
                   : <FiClock size={12}/>;
        return (
          <span className={`status-badge ${getTaskStatusClass(row.status)}`}>
            {icon} {m.label}
          </span>
        );
      },
    },
    {
      header: '',
      field: 'actions',
      sortable: false,
      render: (row) => (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>

          {/* Edit this task */}
          <button
            className="btn-action btn-edit"
            title="Editar tarefa"
            onClick={e => { e.stopPropagation(); setEditTask(row); }}
          >
            <FiEdit2 size={14} />
          </button>

          {/* Open full line operations modal */}
          <button
            className="btn-action"
            title="Ver todas as tarefas desta linha"
            onClick={e => {
              e.stopPropagation();
              setLineModal({
                orderPublicId:     row.order_public_id,
                matrixLineNo:      row.matrix_line_no,
                lineLabel:         row.entered_product_code || row.erp_product_code || row.sample_order_no,
                lineArticle: {
                  enteredProductCode:  row.entered_product_code,
                  enteredProductColor: row.entered_product_color,
                  erpProductCode:      row.erp_product_code,
                  erpProductColorCode: row.erp_product_color_code,
                },
                sampleOrderTypeId: row.sample_order_type_id,
                orderDate:         row.order_date,
                orderCreatedAt:    row.order_created_at,
              });
            }}
          >
            <FiList size={14} />
          </button>

          {/* Quick-complete */}
          {row.status !== 'COMPLETED' && row.status !== 'CANCELLED' && (
            <button
              className="btn-action btn-done"
              title={t('areas.mark_done', 'Marcar concluído')}
              onClick={e => { e.stopPropagation(); markDone(e, row); }}
            >
              <FiCheckCircle size={15} />
            </button>
          )}
        </div>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="my-tasks-list" style={{ width: '100%', maxWidth: '100%' }}>

      {/* Page header */}
      <div className="page-header">
        <div>
          <h2>{t('areas.my_tasks', 'As Minhas Tarefas')}</h2>
          <p className="page-subtitle">
            {t('areas.my_tasks_subtitle', 'Tarefas de pedidos de amostras atribuídas a si')}
          </p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="order-filters">

        {/* Row 1: dropdowns */}
        <div className="filters-row">

          {/* User */}
          <div className="filter-group">
            <label>Utilizador</label>
            <SearchableSelect
              value={filters.user_id}
              onChange={v => handleFilterChange('user_id', v)}
              options={users.map(u => ({ value: String(u.user_id), label: u.full_name || u.username }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todos os utilizadores"
              isClearable
            />
          </div>

          {/* Area */}
          <div className="filter-group">
            <label>Área</label>
            <SearchableSelect
              value={filters.area_id}
              onChange={v => handleFilterChange('area_id', v)}
              options={areas.map(a => ({ value: a.area_id, code: a.area_code, label: a.area_code }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todas as áreas"
              isClearable
            />
          </div>

          {/* Operation */}
          <div className="filter-group">
            <label>Tarefa</label>
            <SearchableSelect
              value={filters.operation_id}
              onChange={v => handleFilterChange('operation_id', v)}
              options={operations.map(op => ({ value: op.operation_id, label: op.operation_label || op.label }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todas as tarefas"
              isClearable
            />
          </div>

          {/* Customer */}
          <div className="filter-group">
            <label>Cliente</label>
            <SearchableSelect
              value={filters.customer_id}
              onChange={v => handleFilterChange('customer_id', v)}
              options={customers.map(c => ({ value: c.customer_id, label: c.customer_name || c.name }))}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Todos os clientes"
              isClearable
            />
          </div>

          {/* Status */}
          <div className="filter-group">
            <label>Estado</label>
            <SearchableSelect
              value={filters.status}
              onChange={v => handleFilterChange('status', v ?? '')}
              options={STATUS_OPTIONS}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              formatDisplay={o => o.label}
              placeholder="Pendentes & Em Curso"
              isClearable
            />
          </div>



          {/* Order No */}
          <div className="filter-group">
            <label>Nº Pedido</label>

              <FiSearch size={13} className="filter-search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder=""
                value={textDraft.sample_order_no}
                onChange={e => setTextDraft(d => ({ ...d, sample_order_no: e.target.value }))}
                onBlur={() => handleTextCommit('sample_order_no')}
                onKeyDown={e => e.key === 'Enter' && handleTextCommit('sample_order_no')}
              />
  
          </div>

          {/* ERP Product */}
          <div className="filter-group">
            <label>Produto ERP</label>
              <FiSearch size={13} className="filter-search-icon" />
              <input
                type="text"
                className="form-control"
                placeholder=""
                value={textDraft.erp_product_code}
                onChange={e => setTextDraft(d => ({ ...d, erp_product_code: e.target.value }))}
                onBlur={() => handleTextCommit('erp_product_code')}
                onKeyDown={e => e.key === 'Enter' && handleTextCommit('erp_product_code')}
              />
          </div>

          {/* Clear */}
          {hasActiveFilters && (
            <div className="filter-group" style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
              <label style={{ visibility: 'hidden' }}>_</label>
              <button className="btn-secondary btn-sm" onClick={clearFilters}
                style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                <FiX size={13} /> Limpar filtros
              </button>
            </div>
          )}

        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={tasks}
        loading={loading}
        error={error}
        onRetry={loadTasks}
        onRowClick={(row) => setEditTask(row)}
        rowClassName={(row) => isOverdue(row) ? 'task-row-overdue' : ''}
        emptyMessage={t('areas.no_tasks_assigned', 'Nenhuma tarefa atribuída')}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={v => { setItemsPerPage(v); setCurrentPage(1); }}
        />
      )}

      {/* Task edit modal */}
      {editTask && (
        <MyTasksEditModal
          task={editTask}
          onSave={handleSaveTask}
          onCancel={() => setEditTask(null)}
        />
      )}

      {/* Full line operations modal */}
      {lineModal && (
        <LineOperationsModal
          orderPublicId={lineModal.orderPublicId}
          matrixLineNo={lineModal.matrixLineNo}
          lineLabel={lineModal.lineLabel}
          lineArticle={lineModal.lineArticle}
          orderDate={lineModal.orderDate}
          orderCreatedAt={lineModal.orderCreatedAt}
          defaultFilter="SELECTED"
          onClose={() => setLineModal(null)}
        />
      )}

      {/* Toasts */}
      <div className="toast-container">
        {toasts.map(t => (
          <Toast key={t.id} {...t} onClose={removeToast} />
        ))}
      </div>

    </div>
  );
};

export default MyTasksList;